import { toast } from "react-toastify";
import { apiClient, apiClientRaw } from "../../api/httpClient";

const USER_EXISTS_MSG = "vermost portal user with this username already exists.";

/**
 * Shared username/password toast formatting for the "Invalid X: ..." style
 * error blocks (used by the invite, internal/team-member, and pgContact flows).
 * @param {object} [userErrors]
 */
function showUserCredentialErrors(userErrors) {
  if (!userErrors) return;

  if (userErrors.username) {
    userErrors.username.forEach((msg) =>
      toast.error(
        `Invalid Username: ${
          msg === USER_EXISTS_MSG
            ? "Username already taken. Please try another one"
            : msg
        }`
      )
    );
  }
  if (userErrors.password) {
    userErrors.password.forEach((msg) => toast.error(`Invalid Password: ${msg}`));
  }
}

/**
 * Surface apiClientRaw non-2xx payloads (validateStatus never rejects).
 * @param {object} [errorData]
 */
function showRegisterRawNon2xxErrors(errorData) {
  let errorShown = false;

  if (errorData?.user) {
    Object.entries(errorData.user).forEach(([field, messages]) => {
      messages.forEach((msg) => {
        const formatted =
          field === "username" && String(msg).includes("already exists")
            ? "Username already taken."
            : msg;
        toast.error(
          `${field.charAt(0).toUpperCase() + field.slice(1)}: ${formatted}`
        );
        errorShown = true;
      });
    });
  }
  if (errorData?.error) {
    toast.error(errorData.error);
    errorShown = true;
  }
  if (errorData?.detail) {
    toast.error(errorData.detail);
    errorShown = true;
  }
  if (!errorShown) {
    toast.error("Registration failed. Please try again or contact support.");
  }
}

/**
 * Verbatim POST/GET behavior from legacy per-page handlers (Phase 8).
 * @typedef {{ navigate: Function, toast: typeof toast }} SubmitCtx
 */

export async function submitProfessionalGuardian({ formData }, ctx) {
  const { navigate } = ctx;
  const requestData = {
    user: {
      username: formData.username,
      password: formData.password,
    },
    email_address: formData.email_address,
    full_name: formData.full_name?.trim() || formData.username?.trim() || "Registered User",
  };

  await apiClient("/register-professional-guardian", "POST", requestData);

  toast.success("Thank you for registering. Please login to continue.");
  navigate("/");
}

export async function submitLawFirmUser({ formData, token }, ctx) {
  const { navigate } = ctx;
  const response = await apiClientRaw(`/lawfirm-user-register/${token}`, "POST", {
    user: {
      username: formData.username,
      email: formData.email,
      password: formData.password,
    },
  });

  if (response && response.status >= 200 && response.status < 300) {
    toast.success("Thank you for registering. Please login to continue.");
    navigate("/");
  } else if (response) {
    showRegisterRawNon2xxErrors(response.data);
  }
}

export async function submitInternalUser({ formData, token }, ctx) {
  const { navigate } = ctx;
  const response = await apiClientRaw(`/register-internal-user/${token}`, "POST", {
    user: {
      username: formData.username,
      password: formData.password,
    },
    full_name: formData.name,
  });

  if (response && response.status >= 200 && response.status < 300) {
    toast.success("Internal User registered successfully");
    navigate("/");
  } else if (response) {
    showRegisterRawNon2xxErrors(response.data);
  }
}

export async function submitTeamMember({ formData, token }, ctx) {
  const { navigate } = ctx;
  const response = await apiClientRaw(`/carrier-team-member/create/${token}`, "POST", {
    user: {
      username: formData.username,
      password: formData.password,
      email: formData.email,
    },
    team_member_name: formData.name,
    team_member_phonenumber: formData.phonenumber,
  });

  if (response && response.status >= 200 && response.status < 300) {
    toast.success("Carrier Team Member registered successfully");
    navigate("/");
  } else if (response) {
    showRegisterRawNon2xxErrors(response.data);
  }
}

function showProfessionalGuardianInviteErrors(errorData) {
  let errorShown = false;

  if (errorData?.user) {
    if (errorData.user.username || errorData.user.password) {
      errorShown = true;
    }
    showUserCredentialErrors(errorData.user);
    if (errorData.user.email) {
      errorData.user.email.forEach((msg) =>
        toast.error(`Invalid Email: ${msg}`)
      );
      errorShown = true;
    }
  }
  if (errorData?.error) {
    toast.error(errorData.error);
    errorShown = true;
  }
  if (errorData?.detail) {
    toast.error(errorData.detail);
    errorShown = true;
  }
  if (!errorShown) {
    toast.error("Registration failed. Please try again or contact support.");
  }
}

export async function submitProfessionalGuardianInvite({ formData, token }, ctx) {
  const { navigate } = ctx;

  const response = await apiClientRaw(`/register-by-invite/${token}/`, "POST", {
    user: {
      username: formData.username,
      password: formData.password,
    },
    email_address: formData.email_address,
    full_name: formData.full_name?.trim() || formData.username?.trim() || "Registered User",
  });

  if (!response) return;

  if (response.status >= 200 && response.status < 300) {
    toast.success("Professional guardian registered successfully");
    navigate("/");
    return;
  }

  showProfessionalGuardianInviteErrors(response.data);
}

export async function submitPgContact({ formData, includePhone }, ctx) {
  const { navigate } = ctx;
  const registrationData = {
    token: formData.token,
    username: formData.username,
    password: formData.password,
    email: formData.email,
  };

  if (includePhone) {
    registrationData.phone_number = formData.phone_number;
  }

  const response = await apiClient("/pg-contacts-register/", "POST", registrationData);

  if (response) {
    toast.success("Professional Guardian Contact registered successfully");
    navigate("/");
  }
}

function mapRawUserNestedErrors(errorData) {
  if (!errorData?.user) return;
  Object.entries(errorData.user).forEach(([field, messages]) => {
    messages.forEach((msg) => {
      const formatted =
        field === "username" && String(msg).includes("already exists")
          ? "Username already taken."
          : msg;
      toast.error(`${field.charAt(0).toUpperCase() + field.slice(1)}: ${formatted}`);
    });
  });
}

function handleLawFirmUserAxiosError(errorData) {
  if (errorData?.user) {
    mapRawUserNestedErrors(errorData);
  } else if (errorData?.error) {
    toast.error(errorData.error);
  } else if (errorData?.detail) {
    toast.error(errorData.detail);
  } else {
    toast.error("An error occurred. Please try again.");
  }
}

function handleInternalOrTeamMemberAxiosError(error, errorData) {
  if (!error.response || !errorData) {
    toast.error("An error occurred during registration. Please try again.");
    return;
  }

  if (errorData.user) {
    showUserCredentialErrors(errorData.user);
  }
  if (errorData.fullname) {
    errorData.fullname.forEach((msg) => toast.error(`Full Name: ${msg}`));
  }
}

/**
 * Axios-style error normalization for variants using apiClientRaw.
 * @param {import("axios").AxiosError | Error} error
 * @param {string} variantId
 */
export function handleRegisterAxiosError(error, variantId) {
  const errorData = error.response?.data;

  if (variantId === "lawFirmUser") {
    handleLawFirmUserAxiosError(errorData);
    return;
  }

  if (variantId === "internalUser" || variantId === "teamMember") {
    handleInternalOrTeamMemberAxiosError(error, errorData);
    return;
  }

  if (variantId === "professionalGuardianInvite") {
    console.error("Registration error:", error);
    toast.error("An error occurred during registration. Please try again.");
  }
}

function handlePgContactApiClientError(errorData) {
  if (!errorData) {
    toast.error("An error occurred during registration. Please try again.");
    return;
  }
  if (errorData.error === "Cannot register a user whose contact has been deleted") {
    toast.error("Invalid invite link. Please contact Admin.");
    return;
  }

  if (errorData.user) {
    showUserCredentialErrors(errorData.user);
  }
  if (errorData.email) {
    errorData.email.forEach((msg) => toast.error(`Email: ${msg}`));
  }
  if (errorData.phone_number) {
    errorData.phone_number.forEach((msg) =>
      toast.error(`Phone Number: ${msg}`)
    );
  }
}

/**
 * ApiClient rejection shape (used by PG contact + public register).
 */
export function handleRegisterApiClientError(error, variantId, formSnapshot) {
  if (variantId === "professionalGuardian") {
    console.error("Registration error:", error);
    const message =
      error?.detail ||
      error?.message ||
      error?.non_field_errors?.[0] ||
      "Registration failed. Please try again.";
    toast.error(message);
    return;
  }

  if (variantId === "pgContact") {
    handlePgContactApiClientError(error);
  }
}

export const REGISTER_SUBMITTERS = Object.freeze({
  professionalGuardian: submitProfessionalGuardian,
  lawFirmUser: submitLawFirmUser,
  internalUser: submitInternalUser,
  teamMember: submitTeamMember,
  professionalGuardianInvite: submitProfessionalGuardianInvite,
  pgContact: submitPgContact,
});
